'use client';

import { useCallback, useState } from 'react';
import { useRouter } from 'next/navigation';
import { trackSignupStarted } from '@/lib/utils/analytics';

import {
  getSignupUrl,
  type LandingBillingCycle,
  type LandingPlanTier,
} from '@/lib/landing/signup-url';

export { getSignupUrl };
export type { LandingBillingCycle, LandingPlanTier };

/**
 * Shared CTA logic for landing page buttons.
 * Landing page visitors are always unauthenticated (authenticated users
 * are redirected to dashboard by middleware), so we always route to sign-up.
 * After signup, the onboarding flow handles plan selection and checkout.
 */
export function useLandingCta() {
  const router = useRouter();
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const startPlanFlow = useCallback(
    ({
      plan = 'starter',
      billingCycle = 'monthly',
      source = 'landing',
    }: {
      plan?: LandingPlanTier;
      billingCycle?: LandingBillingCycle;
      source?: string;
    }) => {
      setError(null);
      const key = `${plan}-${billingCycle}`;
      setLoadingKey(key);

      // Track the signup attempt
      trackSignupStarted(plan, source);

      // Build redirect URL for post-signup onboarding
      const redirectUrl =
        plan === 'free'
          ? '/onboarding?plan=free'
          : `/onboarding?plan=${plan}&billing=${billingCycle}`;

      // Route to sign-up with redirect back to onboarding
      router.push(`/sign-up?redirect_url=${encodeURIComponent(redirectUrl)}`);
    },
    [router]
  );

  return {
    startPlanFlow,
    loadingKey,
    error,
  };
}
